import { debug } from '../../utils/logger';
import BaseEngine from '../BaseEngine';
let Hls;
export default class HLSEngine extends BaseEngine {
    static name = 'hls';
    static canPlayType(mimeType) {
        switch (mimeType) {
            case 'audio/x-mpegurl':
            case 'application/x-mpegurl':
            case 'application/vnd.apple.mpegurl':
                return true;
            default:
                return false;
        }
    }
    #hls;
    #isLive = false;
    #defaultStartPosition = 0;
    constructor(options, defaultStartPosition) {
        super(options);
        if (defaultStartPosition) {
            this.#defaultStartPosition = defaultStartPosition;
        }
    }
    #onLoadError = (error) => {
        this.#deregisterHlsEvents();
        this.#hls?.destroy();
        this.emit('error', error || this.getElement()?.error);
    };
    async #loadHls() {
        try {
            Hls = await import('hls.js').then((module) => module.default);
            const hlsConfig = {
                debug: this._options.debug === 'verbose',
                startPosition: this._options.startPosition ?? this.#defaultStartPosition,
                autoStartLoad: false,
            };
            this.#hls = new Hls(hlsConfig);
        }
        catch (e) {
            this.#onLoadError(new Error('The hls.js library failed to load'));
            throw undefined;
        }
    }
    #attachMedia() {
        return new Promise((resolve, reject) => {
            const resolver = () => {
                resolve();
                this.#hls?.off(Hls.Events.MEDIA_ATTACHED, resolver);
                this.#hls?.off(Hls.Events.ERROR, rejecter);
            };
            const rejecter = () => {
                reject();
                this.#hls?.off(Hls.Events.MEDIA_ATTACHED, resolver);
                this.#hls?.off(Hls.Events.ERROR, rejecter);
            };
            this.#hls?.once(Hls.Events.MEDIA_ATTACHED, resolver);
            this.#hls?.once(Hls.Events.ERROR, rejecter);
            this.#hls?.attachMedia(this._element);
        });
    }
    async #loadSource() {
        await new Promise((resolve, reject) => {
            const resolver = () => {
                resolve();
                this.#hls?.off(Hls.Events.MANIFEST_PARSED, resolver);
                this.#hls?.off(Hls.Events.ERROR, rejecter);
            };
            const rejecter = () => {
                reject();
                this.#hls?.off(Hls.Events.MANIFEST_PARSED, resolver);
                this.#hls?.off(Hls.Events.ERROR, rejecter);
            };
            this.#hls?.once(Hls.Events.MANIFEST_PARSED, resolver);
            this.#hls?.once(Hls.Events.ERROR, rejecter);
            this.#hls?.loadSource(this._options.src);
        });
        this.#hls?.startLoad(this._options.startPosition ?? this.#defaultStartPosition);
    }
    async load() {
        if (!this._element) {
            return;
        }
        await this.#loadHls();
        return new Promise(async (resolve, reject) => {
            const onError = (event, data) => {
                this.#onError(event, data);
                reject();
            };
            this.#hls?.once(Hls.Events.ERROR, onError);
            this.#hls?.once(Hls.Events.LEVEL_LOADED, this.#onLevelLoaded);
            try {
                await Promise.all([this.#loadSource(), this.#attachMedia()]);
                resolve();
            }
            catch (e) {
                this.#onLoadError(e);
                reject();
            }
            this.#hls?.off(Hls.Events.ERROR, onError);
        });
    }
    play() {
        this.#registerHlsEvents();
        return super.play();
    }
    stop() {
        const element = this.getElement();
        if (element) {
            element.currentTime = 0;
        }
        this.#deregisterHlsEvents();
        this.#hls?.stopLoad();
        this.#hls?.detachMedia();
        this.#hls?.destroy();
        super.stop();
    }
    isLive() {
        return this.#isLive;
    }
    #onLevelLoaded = (_, data) => {
        if (data.details.live) {
            this.#isLive = true;
        }
    };
    #onFragLoaded = (event, data) => {
        this.emit('meta', {
            tags: data.frag.tagList.map((tag) => ({
                key: tag[0],
                value: tag[1],
            })),
        });
    };
    #onFragParsingMetadata = (event, data) => {
        this.emit('metadata', { id3: data.frag });
        this.emit('meta', {
            tags: data.frag.tagList.map((tag) => ({
                key: tag[0],
                value: tag[1],
            })),
        });
    };
    #onErrorDetails(details) {
        switch (details) {
            case Hls.ErrorDetails.FRAG_LOAD_ERROR: {
                debug('[HLS] FRAG_LOAD_ERROR', details);
                break;
            }
            case Hls.ErrorDetails.BUFFER_STALLED_ERROR: {
                debug('[HLS] BUFFER_STALLED_ERROR', details);
                break;
            }
            default:
                break;
        }
    }
    #onError = (event, data) => {
        debug('[HLS] Error', data);
        if (data?.details) {
            this.#onErrorDetails(data.details);
        }
        if (!data?.fatal) {
            return;
        }
        if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            debug('[HLS] Fatal media error encountered, try to recover', event);
            this.#hls?.recoverMediaError();
            return;
        }
        const { type, details, response, error } = data || {};
        this.#deregisterHlsEvents();
        this.#hls?.destroy();
        this.emit('error', { type, details, response, message: error?.message });
    };
    #registerHlsEvents() {
        this.#hls?.on(Hls.Events.ERROR, this.#onError);
        this.#hls?.on(Hls.Events.LEVEL_LOADED, this.#onLevelLoaded);
        this.#hls?.on(Hls.Events.FRAG_LOADED, this.#onFragLoaded);
        this.#hls?.on(Hls.Events.FRAG_PARSING_METADATA, this.#onFragParsingMetadata);
    }
    #deregisterHlsEvents() {
        this.#hls?.off(Hls.Events.ERROR, this.#onError);
        this.#hls?.off(Hls.Events.LEVEL_LOADED, this.#onLevelLoaded);
        this.#hls?.off(Hls.Events.FRAG_LOADED, this.#onFragLoaded);
        this.#hls?.off(Hls.Events.FRAG_PARSING_METADATA, this.#onFragParsingMetadata);
    }
}
